import type { OrgMember } from '@services/api/r2ApiClient';
import type { ConnectionGrant, ConnectionPermission, SavedConnection } from '@/types/connection.types';

type AccessChipColor = 'success' | 'warning' | 'error' | 'default' | 'info';

export interface MemberStorageAccessSummary {
  hasUploadAccess: boolean;
  hasFullStorageAccess: boolean;
  actionLabel: string | null;
  chip: {
    label: string;
    color: AccessChipColor;
    variant: 'filled' | 'outlined';
    tooltip: string;
  } | null;
}

function normalizeGrantPrefix(value = ''): string {
  const raw = String(value || '').replace(/\\/g, '/').trim();
  if (!raw || raw === '/') return '';
  return `${raw.split('/').filter(Boolean).join('/')}/`;
}

function grantHasPermission(grant: ConnectionGrant | undefined, permission: ConnectionPermission): boolean {
  return !!grant?.permissions?.includes(permission);
}

function grantIncludesRoot(grant: ConnectionGrant | undefined): boolean {
  const prefixes = grant?.allowedPrefixes?.length ? grant.allowedPrefixes : [''];
  return prefixes.some((prefix) => normalizeGrantPrefix(prefix) === '');
}

function grantHasFullStorageAccess(grant: ConnectionGrant | undefined): boolean {
  return !!grant
    && grantHasPermission(grant, 'read')
    && grantHasPermission(grant, 'write')
    && grantHasPermission(grant, 'delete')
    && grantIncludesRoot(grant);
}

export function getMemberStorageAccessSummary(
  member: Pick<OrgMember, 'status' | 'connectionGrants'>,
  connections: SavedConnection[],
): MemberStorageAccessSummary {
  if (connections.length === 0) {
    return {
      hasUploadAccess: false,
      hasFullStorageAccess: true,
      actionLabel: null,
      chip: null,
    };
  }

  const grants = new Map((member.connectionGrants || []).map((grant) => [grant.connectionId, grant]));
  const matchingGrants = connections
    .map((connection) => grants.get(connection.id))
    .filter((grant): grant is ConnectionGrant => !!grant);
  const writeGrantCount = matchingGrants.filter((grant) => grantHasPermission(grant, 'write')).length;
  const rootWriteGrantCount = matchingGrants.filter((grant) => grantHasPermission(grant, 'write') && grantIncludesRoot(grant)).length;
  const hasFullStorageAccess = connections.every((connection) => grantHasFullStorageAccess(grants.get(connection.id)));
  const isApproved = member.status === 'approved';

  if (!isApproved) {
    return {
      hasUploadAccess: false,
      hasFullStorageAccess,
      actionLabel: hasFullStorageAccess ? null : 'Grant storage access',
      chip: {
        label: member.status === 'blocked' ? 'Blocked' : 'Approval needed',
        color: member.status === 'blocked' ? 'error' : 'warning',
        variant: 'outlined',
        tooltip: writeGrantCount > 0
          ? 'Storage grants include upload, but uploads remain blocked until account status is approved.'
          : 'Account status must be approved before storage grants can be used.',
      },
    };
  }

  if (hasFullStorageAccess) {
    return {
      hasUploadAccess: true,
      hasFullStorageAccess: true,
      actionLabel: null,
      chip: {
        label: 'Can upload',
        color: 'success',
        variant: 'outlined',
        tooltip: 'Approved with read, write, and delete access at the root of every storage connection.',
      },
    };
  }

  if (writeGrantCount > 0) {
    return {
      hasUploadAccess: true,
      hasFullStorageAccess: false,
      actionLabel: 'Grant full access',
      chip: {
        label: rootWriteGrantCount > 0 ? 'Can upload' : 'Folder-limited upload',
        color: rootWriteGrantCount > 0 ? 'success' : 'warning',
        variant: 'outlined',
        tooltip: rootWriteGrantCount > 0
          ? 'Approved with upload access on at least one storage connection.'
          : 'Approved with upload access only inside assigned folder prefixes.',
      },
    };
  }

  if (matchingGrants.length > 0) {
    return {
      hasUploadAccess: false,
      hasFullStorageAccess: false,
      actionLabel: 'Grant upload access',
      chip: {
        label: 'Read only',
        color: 'default',
        variant: 'outlined',
        tooltip: 'The user can view assigned storage but does not have write permission.',
      },
    };
  }

  return {
    hasUploadAccess: false,
    hasFullStorageAccess: false,
    actionLabel: 'Grant storage access',
    chip: null,
  };
}
